import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  ArrowRight, 
  ShoppingBag, 
  Store, 
  Truck, 
  Star, 
  Clock, 
  MapPin,
  Zap,
  Shield,
  CheckCircle,
  ChevronRight,
  Quote,
  Flame,
  Mail,
  ShoppingBasket,
  Smartphone,
  Shirt,
  Home,
  Pill,
  BookOpen,
  ChevronLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Star Rating Component with visual breakdown
const StarRating = ({ rating, size = 16, showCount = false, count = 0 }) => {
  const fullStars = Math.floor(rating);
  const hasHalf = rating % 1 >= 0.5;
  
  return (
    <div className="flex items-center gap-1">
      <div className="flex">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            size={size}
            className={i < fullStars ? "fill-amber-400 text-amber-400" : (i === fullStars && hasHalf ? "fill-amber-400/50 text-amber-400" : "fill-gray-200 text-gray-200")}
          />
        ))}
      </div>
      {showCount && (
        <span className="text-sm text-gray-500 ml-1">({count})</span>
      )}
    </div>
  );
};

// Rating Breakdown Component
const RatingBreakdown = ({ breakdown, totalReviews }) => {
  return (
    <div className="space-y-2">
      {[5, 4, 3, 2, 1].map((star) => {
        const count = breakdown?.[star.toString()] || 0;
        const percentage = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
        return (
          <div key={star} className="flex items-center gap-3">
            <span className="text-sm font-medium w-4 text-gray-600">{star}</span>
            <Star size={14} className="fill-amber-400 text-amber-400" />
            <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-amber-400 rounded-full transition-all duration-500"
                style={{ width: `${percentage}%` }}
              />
            </div>
            <span className="text-xs text-gray-500 w-8">{count}</span>
          </div>
        );
      })}
    </div>
  );
};

// Icon mapping for categories
const iconMap = {
  ShoppingBasket: ShoppingBasket,
  Smartphone: Smartphone,
  Shirt: Shirt,
  Home: Home,
  Pill: Pill,
  BookOpen: BookOpen
};

// Hero Carousel Slides - Indian Village Shops
const heroSlides = [
  {
    image: "https://images.unsplash.com/photo-1739066598279-1297113f5c6a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjB2aWxsYWdlJTIwa2lyYW5hJTIwc2hvcCUyMHN0b3JlfGVufDB8fHx8MTc3NTI0MDUzNXww&ixlib=rb-4.1.0&q=85&w=1600",
    title: "गांव की दुकान,",
    subtitle: "घर का सामान!",
    description: "अपनी पसंदीदा लोकल दुकानों से ताज़ा किराना, इलेक्ट्रॉनिक्स और फैशन मंगवाएं।"
  },
  {
    image: "https://images.unsplash.com/photo-1769685104379-0bedf8505586?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwzfHxpbmRpYW4lMjBsb2NhbCUyMG1hcmtldCUyMGdyb2NlcnklMjBzaG9wfGVufDB8fHx8MTc3NTI0MDUzNXww&ixlib=rb-4.1.0&q=85&w=1600",
    title: "ताज़ा सब्ज़ी,",
    subtitle: "सीधे आपके घर!",
    description: "Fresh vegetables and groceries from local markets delivered to your doorstep."
  },
  {
    image: "https://images.pexels.com/photos/35704478/pexels-photo-35704478.jpeg?auto=compress&cs=tinysrgb&w=1600",
    title: "आपकी अपनी,",
    subtitle: "किराना दुकान!",
    description: "Your trusted neighborhood kirana store, now just a click away."
  }
];

// Categories data
const categoriesData = [
  { name: "Groceries", icon: "ShoppingBasket", shop_count: 45, gradient: "from-emerald-400 to-emerald-600" },
  { name: "Electronics", icon: "Smartphone", shop_count: 28, gradient: "from-blue-400 to-blue-600" },
  { name: "Fashion", icon: "Shirt", shop_count: 52, gradient: "from-pink-400 to-pink-600" },
  { name: "Home & Kitchen", icon: "Home", shop_count: 31, gradient: "from-orange-400 to-orange-600" },
  { name: "Pharmacy", icon: "Pill", shop_count: 19, gradient: "from-red-400 to-red-600" },
  { name: "Books", icon: "BookOpen", shop_count: 15, gradient: "from-purple-400 to-purple-600" }
];

// Customer reviews with photos
const customerReviews = [
  {
    id: 1,
    user_name: "Priya Sharma",
    user_avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100",
    rating: 5,
    comment: "Amazing quality groceries! Fresh vegetables delivered in just 25 minutes. Will order again!",
    photo_url: "https://images.unsplash.com/photo-1610636996379-4d184e2ef20a?w=400&q=80"
  },
  {
    id: 2,
    user_name: "Rahul Verma",
    user_avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100",
    rating: 5,
    comment: "Got my headphones delivered same day. Packaging was perfect!",
    photo_url: "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=400&q=80"
  },
  {
    id: 3,
    user_name: "Anita Patel",
    user_avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100",
    rating: 4,
    comment: "Love the fashion collection at Style Junction. Great ethnic wear options!",
    photo_url: "https://images.unsplash.com/photo-1558171813-4c088753af8f?w=400&q=80"
  }
];

const Landing = () => {
  const { t, language } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);

  // Hero Carousel Slides with translations
  const heroSlides = [
    {
      image: "https://images.unsplash.com/photo-1739066598279-1297113f5c6a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjB2aWxsYWdlJTIwa2lyYW5hJTIwc2hvcCUyMHN0b3JlfGVufDB8fHx8MTc3NTI0MDUzNXww&ixlib=rb-4.1.0&q=85&w=1600",
      title: t('heroTitle'),
      subtitle: t('heroSubtitle'),
      description: t('heroDescription')
    },
    {
      image: "https://images.unsplash.com/photo-1769685104379-0bedf8505586?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwzfHxpbmRpYW4lMjBsb2NhbCUyMG1hcmtldCUyMGdyb2NlcnklMjBzaG9wfGVufDB8fHx8MTc3NTI0MDUzNXww&ixlib=rb-4.1.0&q=85&w=1600",
      title: language === 'hi' ? "ताज़ा सब्ज़ी," : "Fresh Veggies,",
      subtitle: language === 'hi' ? "सीधे आपके घर!" : "To Your Door!",
      description: t('heroDescription')
    },
    {
      image: "https://images.pexels.com/photos/35704478/pexels-photo-35704478.jpeg?auto=compress&cs=tinysrgb&w=1600",
      title: language === 'hi' ? "आपकी अपनी," : "Your Own,",
      subtitle: language === 'hi' ? "किराना दुकान!" : "Kirana Store!",
      description: t('heroDescription')
    }
  ];

  // Categories with translations
  const categoriesData = [
    { name: t('groceries'), icon: "ShoppingBasket", shop_count: 45, gradient: "from-emerald-400 to-emerald-600" },
    { name: t('electronics'), icon: "Smartphone", shop_count: 28, gradient: "from-blue-400 to-blue-600" },
    { name: t('fashion'), icon: "Shirt", shop_count: 52, gradient: "from-pink-400 to-pink-600" },
    { name: t('homeKitchen'), icon: "Home", shop_count: 31, gradient: "from-orange-400 to-orange-600" },
    { name: t('pharmacy'), icon: "Pill", shop_count: 19, gradient: "from-red-400 to-red-600" },
    { name: t('books'), icon: "BookOpen", shop_count: 15, gradient: "from-purple-400 to-purple-600" }
  ];

  useEffect(() => {
    fetchShops();
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  const fetchShops = async () => {
    try {
      const response = await axios.get(`${API}/shops`);
      setShops(response.data || []);
    } catch (error) {
      console.error('Failed to load shops:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Carousel */}
      <section className="relative" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
          <div className="relative h-[500px] md:h-[600px] rounded-3xl overflow-hidden shadow-2xl">
            {/* Carousel Images */}
            {heroSlides.map((slide, index) => (
              <div
                key={index}
                className={`absolute inset-0 transition-opacity duration-700 ${
                  index === currentSlide ? "opacity-100" : "opacity-0"
                }`}
              >
                <img
                  src={slide.image}
                  alt={slide.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
              </div>
            ))}

            {/* Content Overlay */}
            <div className="absolute inset-0 flex flex-col justify-end p-8 md:p-12">
              {/* Fast Delivery Badge */}
              <div className="absolute top-6 left-6 bg-white/90 backdrop-blur-md px-4 py-2 rounded-full flex items-center gap-2 shadow-lg">
                <Zap className="text-orange-500" size={18} />
                <span className="text-sm font-semibold text-gray-900">{t('fastestDelivery')}</span>
              </div>

              {/* Stats Badge */}
              <div className="absolute top-6 right-6 bg-white/90 backdrop-blur-md px-4 py-3 rounded-xl shadow-lg">
                <div className="flex items-center gap-2">
                  <Zap className="text-amber-500" size={16} />
                  <span className="text-sm font-bold text-gray-900">{t('fastDelivery')}</span>
                </div>
                <span className="text-xs text-gray-500">{t('under30mins')}</span>
              </div>

              <div className="max-w-xl">
                <h1 className="text-4xl md:text-6xl font-black text-white leading-none tracking-tight mb-2">
                  {heroSlides[currentSlide].title}
                </h1>
                <h1 className="text-4xl md:text-6xl font-black text-orange-400 leading-none tracking-tight mb-6">
                  {heroSlides[currentSlide].subtitle}
                </h1>
                <p className="text-white/90 text-lg mb-8">
                  {heroSlides[currentSlide].description}
                </p>

                <div className="flex flex-wrap gap-4">
                  <Link to="/auth">
                    <Button 
                      data-testid="get-started-btn"
                      className="bg-orange-500 hover:bg-orange-600 text-white h-14 px-8 rounded-full shadow-xl transition-all duration-300 font-bold text-lg"
                    >
                      {t('startShopping')}
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/auth">
                    <Button 
                      data-testid="become-partner-btn"
                      className="bg-white/20 backdrop-blur-md border-2 border-white/40 text-white hover:bg-white/30 h-14 px-8 rounded-full transition-all duration-300 font-bold text-lg"
                    >
                      {t('joinCommunity')}
                    </Button>
                  </Link>
                </div>
              </div>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-6 mt-8">
                <div className="flex items-center gap-3 text-white/90">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center">
                    <Shield size={22} />
                  </div>
                  <div>
                    <p className="font-bold text-lg">100+</p>
                    <p className="text-xs opacity-80">{t('verifiedShops')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-white/90">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center">
                    <CheckCircle size={22} />
                  </div>
                  <div>
                    <p className="font-bold text-lg">{language === 'hi' ? 'सुरक्षित' : 'Secure'}</p>
                    <p className="text-xs opacity-80">{language === 'hi' ? 'भुगतान' : 'Payments'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Carousel Dots */}
            <div className="absolute bottom-6 right-6 flex gap-2">
              {heroSlides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentSlide ? "bg-white w-6" : "bg-white/50"
                  }`}
                  data-testid={`carousel-dot-${index}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 bg-gray-50" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">{t('exploreCategories')}</h2>
              <p className="text-gray-500 mt-1">{t('findWhatYouNeed')}</p>
            </div>
            <button className="text-orange-500 font-semibold flex items-center gap-1 hover:gap-2 transition-all" data-testid="view-all-categories">
              {t('viewAll')} <ChevronRight size={18} />
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {categoriesData.map((category, index) => {
              const IconComponent = iconMap[category.icon] || ShoppingBasket;
              return (
                <Link
                  to="/auth"
                  key={category.name}
                  className="group bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 text-center"
                  data-testid={`category-${category.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className={`w-16 h-16 mx-auto mb-4 bg-gradient-to-br ${category.gradient} rounded-2xl flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <IconComponent size={28} />
                  </div>
                  <h3 className="font-semibold text-gray-900">{category.name}</h3>
                  <p className="text-sm text-gray-500 mt-1">{category.shop_count} {t('shops').toLowerCase()}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Shops Section */}
      <section className="py-16" data-testid="featured-shops-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">{t('featuredShops')}</h2>
              <p className="text-gray-500 mt-1">{t('handpickedStores')}</p>
            </div>
            <span className="text-sm text-gray-500 uppercase tracking-wider font-semibold">
              {shops.length} {t('shopsFound')}
            </span>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : shops.length === 0 ? (
            <div className="text-center py-12">
              <Store className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">{language === 'hi' ? 'अभी कोई दुकान उपलब्ध नहीं। पहले जुड़ें!' : 'No shops available yet. Be the first to join!'}</p>
              <Link to="/auth">
                <Button className="mt-4 bg-orange-500 hover:bg-orange-600 text-white rounded-full">
                  {language === 'hi' ? 'अपनी दुकान रजिस्टर करें' : 'Register Your Shop'}
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {shops.map((shop) => (
                <Link
                  to={`/shop/${shop.id}`}
                  key={shop.id}
                  className="group bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                  data-testid={`shop-card-${shop.id}`}
                >
                  {/* Image Container */}
                  <div className="relative h-48 overflow-hidden bg-gradient-to-br from-emerald-100 to-orange-100">
                    {shop.image_url ? (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${shop.image_url}`}
                        alt={shop.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Store className="w-16 h-16 text-emerald-500/50" />
                      </div>
                    )}
                    
                    {/* Category Badge */}
                    <div className="absolute top-4 left-4 bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      {t('localShop')}
                    </div>

                    {/* Rating Badge */}
                    <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-md px-3 py-1 rounded-full flex items-center gap-1 shadow-lg">
                      <Star className="fill-amber-400 text-amber-400" size={14} />
                      <span className="font-bold text-sm text-gray-900">{shop.rating?.toFixed(1) || '4.5'}</span>
                    </div>

                    {/* Social Proof Badge */}
                    <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-md px-3 py-1 rounded-full flex items-center gap-1 text-xs font-semibold shadow-sm">
                      <Flame className="text-orange-500" size={12} />
                      <span className="text-gray-900">{t('popular')}</span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-bold text-lg text-gray-900 group-hover:text-orange-500 transition-colors">
                        {shop.name}
                      </h3>
                      <ChevronRight className="text-gray-400 group-hover:text-orange-500 transition-colors" size={20} />
                    </div>

                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">{shop.description}</p>

                    <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <Clock size={14} />
                        <span>20-30 min</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <MapPin size={14} />
                        <span>2.4 km</span>
                      </div>
                    </div>

                    {/* Rating with count */}
                    <div className="mt-3">
                      <StarRating rating={shop.rating || 4.5} showCount count={shop.total_reviews || 50} />
                    </div>

                    {/* Recent orders social proof */}
                    <div className="flex items-center gap-2 mt-4">
                      <div className="flex -space-x-2">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="w-6 h-6 rounded-full bg-gray-100 border-2 border-white overflow-hidden">
                            <img 
                              src={`https://images.unsplash.com/photo-${1494790108377 + i}-be9c29b29330?w=50`} 
                              alt="" 
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ))}
                      </div>
                      <span className="text-xs text-gray-500">50+ {t('peopleOrderedRecently')}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Customer Photo Reviews Section */}
      <section className="py-16 bg-gray-900 text-white" data-testid="customer-reviews-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-4xl font-bold tracking-tight">{t('customerReviews')}</h2>
            <p className="text-gray-400 mt-2">{t('realPhotosRealReviews')}</p>
          </div>

          {/* Photo Gallery */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-12">
            {customerReviews.filter(r => r.photo_url).map((review, index) => (
              <div 
                key={review.id}
                className="group relative aspect-square rounded-2xl overflow-hidden cursor-pointer"
                data-testid={`review-photo-${index}`}
              >
                <img
                  src={review.photo_url}
                  alt={review.comment}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-4">
                  <StarRating rating={review.rating} size={14} />
                  <p className="text-sm mt-2 line-clamp-2">{review.comment}</p>
                  <p className="text-xs text-white/60 mt-1">- {review.user_name}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Text Reviews */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {customerReviews.map((review, index) => (
              <div 
                key={review.id}
                className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10"
                data-testid={`review-card-${index}`}
              >
                <Quote className="text-orange-500 mb-4" size={24} />
                <p className="text-white/80 mb-4">{review.comment}</p>
                <div className="flex items-center gap-3">
                  <img
                    src={review.user_avatar}
                    alt={review.user_name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-semibold">{review.user_name}</p>
                    <StarRating rating={review.rating} size={12} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-emerald-50 to-orange-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('simpleEasyFast')}
            </h2>
            <p className="text-lg text-gray-600">
              {t('just3Steps')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-emerald-100">
                <div className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-emerald-600 text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6 shadow-xl">
                  1
                </div>
                <h3 className="font-bold text-2xl text-gray-900 mb-3">{t('browseShopsStep')}</h3>
                <p className="text-gray-600">{t('findLocalShops')}</p>
              </div>
            </div>
            <div className="text-center">
              <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-orange-100">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-400 to-orange-600 text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6 shadow-xl">
                  2
                </div>
                <h3 className="font-bold text-2xl text-gray-900 mb-3">{t('placeOrderStep')}</h3>
                <p className="text-gray-600">{t('addItemsCheckout')}</p>
              </div>
            </div>
            <div className="text-center">
              <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-blue-100">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-400 to-blue-600 text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6 shadow-xl">
                  3
                </div>
                <h3 className="font-bold text-2xl text-gray-900 mb-3">{t('getDeliveredStep')}</h3>
                <p className="text-gray-600">{t('trackReceive')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 bg-orange-500" data-testid="newsletter-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-4xl font-bold text-white tracking-tight mb-4">
            {t('getExclusiveOffers')}
          </h2>
          <p className="text-white/80 mb-8">
            {language === 'hi' ? '5,000+ लोग हर हफ्ते लोकल डील्स और नई दुकानों की जानकारी पाते हैं।' : 'Join 5,000+ people who get weekly updates on local deals and new shop arrivals.'}
          </p>
          
          <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <div className="relative flex-1">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="email"
                placeholder={t('enterEmail')}
                className="w-full pl-12 pr-4 py-4 rounded-full text-gray-900 focus:outline-none focus:ring-2 focus:ring-white"
                data-testid="newsletter-email-input"
              />
            </div>
            <Button 
              type="submit"
              className="bg-gray-900 hover:bg-gray-800 text-white py-4 px-8 rounded-full font-semibold whitespace-nowrap"
              data-testid="newsletter-subscribe-btn"
            >
              {t('subscribe')}
            </Button>
          </form>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-emerald-500 to-orange-500">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="font-bold text-4xl md:text-5xl text-white">
            {t('readyToStart')}
          </h2>
          <p className="text-xl text-white">
            {t('joinThousands')}
            <br />
            <span className="text-yellow-200 font-semibold">{t('thousandsFamilies')}</span>
          </p>
          <Link to="/auth">
            <Button
              data-testid="cta-signup-btn"
              className="bg-white text-emerald-600 hover:bg-yellow-50 h-16 px-12 rounded-full shadow-2xl transition-all duration-300 active:scale-95 text-xl font-bold"
            >
              <ShoppingBag className="w-6 h-6 mr-3" />
              {t('signUpFreeToday')}
              <ArrowRight className="w-6 h-6 ml-3" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-orange-500 rounded-xl flex items-center justify-center">
                  <ShoppingBag className="text-white" size={22} />
                </div>
                <span className="text-xl font-bold">
                  Samaan<span className="text-orange-400">Dena</span>
                </span>
              </div>
              <p className="text-gray-400 text-sm">
                {t('favoriteLocalShops')}
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">{t('quickLinks')}</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">{t('aboutUs')}</a></li>
                <li><a href="#" className="hover:text-white transition-colors">{t('becomePartner')}</a></li>
                <li><a href="#" className="hover:text-white transition-colors">{t('careers')}</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">{t('support')}</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">{t('helpCenter')}</a></li>
                <li><a href="#" className="hover:text-white transition-colors">{t('contactUs')}</a></li>
                <li><a href="#" className="hover:text-white transition-colors">{t('faqs')}</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">{t('legal')}</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">{t('privacyPolicy')}</a></li>
                <li><a href="#" className="hover:text-white transition-colors">{t('termsOfService')}</a></li>
                <li><a href="#" className="hover:text-white transition-colors">{t('refundPolicy')}</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 text-center text-gray-500 text-sm">
            <p>&copy; 2026 SamaanDena. {t('allRightsReserved')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
